/**
 * PARAMÈTRES › RELANCES — lot 160, 04/11/2026.
 *
 * Demande du cadrage de la refonte du Recouvrement (03/11) : « la séquence doit
 * être définie dans les paramètres ». Seul le délai de chaque niveau se règle ;
 * l'ordre, les noms et les frais restent ceux du code
 * (`CADENCE_PL`, `apps/api/src/relances_pl.ts`). Les bornes sont celles de
 * l'API (`fauteDeSequence`), qui a le dernier mot.
 */

import { useCallback, useEffect, useState } from 'react';
import type { EchecApi } from './api.js';
import { enregistrerSequence, lireSequence, type NiveauSequence } from './apiRelances.js';
import type { NiveauPl } from './relancesPl.js';

/** « −5 » → « J−5 », « 2 » → « J+2 », « 0 » → « Jour de l'échéance ». */
export function jourDit(jour: number): string {
  if (jour === 0) return 'Jour de l’échéance';
  return jour < 0 ? `J−${String(-jour)}` : `J+${String(jour)}`;
}

/** Ce qu'on a tapé, lu comme un nombre entier de jours ; `null` sinon. */
export function joursSaisis(t: string): number | null {
  const net = t.trim().replace('−', '-');
  return /^[-+]?\d{1,3}$/.test(net) ? Number(net) : null;
}

/**
 * LE CONTRÔLE AVANT ENVOI, même règle que l'API : l'avis entre J−60 et
 * l'échéance, les relances entre J+1 et J+365, chaque niveau après le
 * précédent. Rend le message, ou `null`.
 */
export function fauteDansLaSaisie(
  niveaux: readonly NiveauSequence[],
  saisie: Readonly<Record<string, string>>,
): string | null {
  let avant: number | null = null;
  for (const n of niveaux) {
    const j = joursSaisis(saisie[n.cle] ?? '');
    if (j === null) return `${n.nom} : un nombre entier de jours.`;
    if (n.cle === 'avis' && (j < -60 || j > 0)) return `${n.nom} : entre J−60 et le jour de l’échéance.`;
    if (n.cle !== 'avis' && (j < 1 || j > 365)) return `${n.nom} : entre J+1 et J+365.`;
    if (avant !== null && j <= avant) return `${n.nom} : après le niveau précédent.`;
    avant = j;
  }
  return null;
}

export function VueSequenceRelance(p: {
  readonly niveaux: readonly NiveauSequence[] | null;
  readonly saisie: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly fait: string | null;
  readonly envoi: boolean;
  readonly quandChamp: (cle: NiveauPl, valeur: string) => void;
  readonly quandEnregistrer: () => void;
  readonly quandDepart: () => void;
}) {
  if (p.niveaux === null) {
    return p.refus === null ? (
      <p className="attente">Chargement de la séquence…</p>
    ) : (
      <p className="bandeau bandeau-refus">{p.refus}</p>
    );
  }
  const change = p.niveaux.some((n) => joursSaisis(p.saisie[n.cle] ?? '') !== n.jour);
  const auDepart = p.niveaux.every((n) => (p.saisie[n.cle] ?? '').trim() === String(n.defaut));
  return (
    <div className="parametres">
      {p.refus !== null && <p className="bandeau bandeau-refus">{p.refus}</p>}
      {p.fait !== null && <p className="attente fait-sequence">{p.fait}</p>}
      <h3 className="titre-indexation">Séquence des relances</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          p.quandEnregistrer();
        }}
      >
        <div className="tableau-cadre">
          <table className="table-sequence">
            <thead>
              <tr>
                <th>Niveau</th>
                <th>Jours après l’échéance</th>
                <th>Part le</th>
                <th>Délai de départ</th>
              </tr>
            </thead>
            <tbody>
              {p.niveaux.map((n) => {
                const j = joursSaisis(p.saisie[n.cle] ?? '');
                return (
                  <tr key={n.cle}>
                    <td>{n.nom}</td>
                    <td>
                      <input
                        className="champ court"
                        type="text"
                        inputMode="numeric"
                        aria-label={`${n.nom} — jours après l’échéance`}
                        value={p.saisie[n.cle] ?? ''}
                        disabled={p.envoi}
                        onChange={(e) => p.quandChamp(n.cle, e.target.value)}
                      />
                    </td>
                    <td>{j === null ? '—' : jourDit(j)}</td>
                    <td className="seq-depart">{jourDit(n.defaut)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="actions-sequence">
          <button className="bouton" type="submit" disabled={p.envoi || !change}>
            Enregistrer
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={p.envoi || auDepart}
            onClick={p.quandDepart}
          >
            Revenir aux délais de départ
          </button>
        </div>
      </form>
    </div>
  );
}

export function OngletSequenceRelance({
  jeton,
  quandAccesPerdu,
  messageRefus,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string | undefined;
}) {
  const [niveaux, setNiveaux] = useState<readonly NiveauSequence[] | null>(null);
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const [refus, setRefus] = useState<string | null>(null);
  const [fait, setFait] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const prendre = (lus: readonly NiveauSequence[]) => {
    setNiveaux(lus);
    setSaisie(Object.fromEntries(lus.map((n) => [n.cle, String(n.jour)])));
  };

  const charger = useCallback(async () => {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const r = await lireSequence(j);
    if (r.etat === 'ok') {
      prendre(r.valeur);
      return;
    }
    if (r.cause === 'acces') quandAccesPerdu();
    // UNE LECTURE QUI ÉCHOUE LE DIT : des champs vides feraient croire que la
    // séquence n'existe pas.
    setRefus(r.message ?? messageRefus(r.cause) ?? 'La séquence n’a pas pu être lue.');
  }, [jeton, quandAccesPerdu, messageRefus]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const enregistrer = () => {
    if (niveaux === null) return;
    setRefus(null);
    setFait(null);
    const faute = fauteDansLaSaisie(niveaux, saisie);
    if (faute !== null) {
      setRefus(faute);
      return;
    }
    const jours = Object.fromEntries(
      niveaux.map((n) => [n.cle, joursSaisis(saisie[n.cle] ?? '') ?? n.jour]),
    ) as Record<NiveauPl, number>;
    setEnvoi(true);
    void (async () => {
      const j = await jeton();
      if (j === null) {
        setEnvoi(false);
        quandAccesPerdu();
        return;
      }
      const r = await enregistrerSequence(j, jours);
      setEnvoi(false);
      if (r.etat === 'ok') {
        prendre(r.valeur);
        setFait('Séquence enregistrée.');
        return;
      }
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? messageRefus(r.cause) ?? 'Stafa a refusé cette séquence.');
    })();
  };

  return (
    <VueSequenceRelance
      niveaux={niveaux}
      saisie={saisie}
      refus={refus}
      fait={fait}
      envoi={envoi}
      quandChamp={(cle, valeur) => {
        setFait(null);
        setSaisie((s) => ({ ...s, [cle]: valeur }));
      }}
      quandEnregistrer={enregistrer}
      quandDepart={() => {
        if (niveaux === null) return;
        setFait(null);
        setSaisie(Object.fromEntries(niveaux.map((n) => [n.cle, String(n.defaut)])));
      }}
    />
  );
}
